/**
 * Accessibility shim for `react-quill` / Quill 1.x.
 *
 * Quill renders its editing surface as a bare `contenteditable` <div> and its
 * toolbar as icon-only <button> elements whose meaning lives entirely in CSS
 * background images. Out of the box that leaves:
 *
 *  - an editor with no role, no name and no "multiline" hint (WCAG 1.3.1 /
 *    4.1.2),
 *  - every toolbar control unnamed — a screen reader announces "button"
 *    (WCAG 4.1.2),
 *  - no exposed pressed state for the format toggles, which Quill tracks only
 *    through its `ql-active` class (WCAG 4.1.2 — Name, Role, *Value*).
 *
 * Quill exposes no options for any of this, so the attributes are applied to
 * the DOM it owns. Nothing visual is touched. A class-only MutationObserver
 * keeps the toggle states in step with Quill's own bookkeeping.
 */

import { useEffect } from "react";

/** Action buttons — a press does something, there is no on/off state. */
const ACTION_LABELS: Record<string, string> = {
  link: "Insert link",
  image: "Insert image",
  video: "Insert video",
  formula: "Insert formula",
  clean: "Clear formatting",
};

/** Toggle buttons — `aria-pressed` mirrors Quill's `ql-active` class. */
const TOGGLE_LABELS: Record<string, string> = {
  bold: "Bold",
  italic: "Italic",
  underline: "Underline",
  strike: "Strikethrough",
  blockquote: "Blockquote",
  "code-block": "Code block",
  script: "Script",
};

/** Buttons that carry a `value`, e.g. `<button class="ql-list" value="bullet">`. */
const VALUE_LABELS: Record<string, Record<string, string>> = {
  list: { ordered: "Numbered list", bullet: "Bulleted list", check: "Checklist" },
  indent: { "-1": "Decrease indent", "+1": "Increase indent" },
  header: { "1": "Heading 1", "2": "Heading 2", "3": "Heading 3" },
  align: { "": "Align left", center: "Align centre", right: "Align right", justify: "Justify" },
  script: { sub: "Subscript", super: "Superscript" },
};

/** Dropdowns Quill builds from a `<select>` (`.ql-picker`). */
const PICKER_LABELS: Record<string, string> = {
  header: "Heading",
  font: "Font",
  size: "Font size",
  align: "Text alignment",
  color: "Text colour",
  background: "Background colour",
};

/** `indent`/`link`/`clean` are one-shot actions, so they take no pressed state. */
const isToggle = (format: string) =>
  format in TOGGLE_LABELS || format === "list" || format === "header";

/** Structural/state classes Quill adds alongside the format class. */
const STRUCTURAL_CLASSES = new Set([
  "picker",
  "picker-label",
  "picker-item",
  "picker-options",
  "active",
  "expanded",
  "selected",
  "flip",
  "snow",
  "bubble",
]);

/** Pulls "bold" out of `ql-bold`, ignoring Quill's structural/state classes. */
const formatOf = (element: Element) =>
  Array.from(element.classList)
    .filter((name) => name.startsWith("ql-"))
    .map((name) => name.slice(3))
    .find((name) => name.length > 0 && !STRUCTURAL_CLASSES.has(name));

const labelFor = (format: string, value: string | null) => {
  if (value !== null && VALUE_LABELS[format]?.[value]) {
    return VALUE_LABELS[format][value];
  }
  return TOGGLE_LABELS[format] ?? ACTION_LABELS[format];
};

const applyButtonNames = (toolbar: HTMLElement) => {
  toolbar.querySelectorAll("button").forEach((button) => {
    // Quill omits it, and an unset type submits any form the editor sits in.
    button.setAttribute("type", "button");

    const format = formatOf(button);
    if (!format) return;

    const label = labelFor(format, button.getAttribute("value"));
    if (label) button.setAttribute("aria-label", label);
  });

  toolbar.querySelectorAll<HTMLElement>(".ql-picker").forEach((picker) => {
    const format = formatOf(picker);
    const label = picker.querySelector<HTMLElement>(".ql-picker-label");
    // Quill already gives the label role="button" + aria-expanded; only the
    // accessible name is missing (the visible text is a CSS ::before).
    if (format && label && PICKER_LABELS[format]) {
      label.setAttribute("aria-label", PICKER_LABELS[format]);
    }

    // Quill gives the options role="button" but their visible text is a CSS
    // ::before fed from data-value, so they read as unnamed buttons.
    picker.querySelectorAll<HTMLElement>(".ql-picker-item").forEach((item) => {
      const value = item.getAttribute("data-value");
      const itemLabel =
        item.getAttribute("data-label") ||
        (format && value !== null && VALUE_LABELS[format]?.[value]) ||
        value ||
        "Normal";
      item.setAttribute("aria-label", itemLabel);
    });
  });
};

const syncToggleStates = (toolbar: HTMLElement) => {
  toolbar.querySelectorAll("button").forEach((button) => {
    const format = formatOf(button);
    if (!format || !isToggle(format)) return;
    button.setAttribute(
      "aria-pressed",
      button.classList.contains("ql-active") ? "true" : "false",
    );
  });
};

export interface QuillAccessibilityOptions {
  /** Accessible name for the editor. Ignored when `labelledBy` is given. */
  label?: string;
  /** Id of the visible label element that names the editor. */
  labelledBy?: string;
  /** Id(s) of hint/error text describing the editor. */
  describedBy?: string;
  required?: boolean;
  /** Skip while the editor is unmounted (e.g. a closed dialog). */
  enabled?: boolean;
  /** Accessible name for the toolbar grouping. */
  toolbarLabel?: string;
}

/**
 * Names and roles the Quill instance held by `quillRef` once it has mounted.
 *
 * @param quillRef ref passed to `<ReactQuill ref={…} />`.
 */
export const useQuillAccessibility = (
  quillRef: React.RefObject<any>,
  {
    label,
    labelledBy,
    describedBy,
    required,
    enabled = true,
    toolbarLabel = "Text formatting",
  }: QuillAccessibilityOptions,
) => {
  useEffect(() => {
    if (!enabled) return;

    let editor: any;
    try {
      editor = quillRef.current?.getEditor?.();
    } catch {
      // react-quill throws while the editor is still being instantiated.
      return;
    }
    if (!editor) return;

    const root: HTMLElement | undefined = editor.root;
    if (root) {
      root.setAttribute("role", "textbox");
      root.setAttribute("aria-multiline", "true");
      if (labelledBy) {
        root.setAttribute("aria-labelledby", labelledBy);
        root.removeAttribute("aria-label");
      } else if (label) {
        root.setAttribute("aria-label", label);
      }
      if (describedBy) root.setAttribute("aria-describedby", describedBy);
      else root.removeAttribute("aria-describedby");
      if (required) root.setAttribute("aria-required", "true");
      else root.removeAttribute("aria-required");
    }

    const toolbar: HTMLElement | undefined =
      editor.getModule?.("toolbar")?.container;
    if (!toolbar) return;

    // Not `role="toolbar"`: that promises arrow-key navigation, which Quill
    // does not implement. A named group is honest about what is there.
    toolbar.setAttribute("role", "group");
    toolbar.setAttribute("aria-label", toolbarLabel);

    applyButtonNames(toolbar);
    syncToggleStates(toolbar);

    // Quill flips `ql-active` as the caret moves; mirror it onto aria-pressed.
    const observer = new MutationObserver(() => syncToggleStates(toolbar));
    observer.observe(toolbar, {
      attributes: true,
      attributeFilter: ["class"],
      subtree: true,
    });

    return () => observer.disconnect();
  }, [quillRef, label, labelledBy, describedBy, required, enabled, toolbarLabel]);
};

export default useQuillAccessibility;
